import { logger } from "@/utils/logger";

import React from 'react';
import { Button } from '@/components/ui/button';
import { DealerManager } from '@/utils/dealerManager';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFormContext } from '@/contexts/FormContext';

interface ReplyOptionsMessageProps {
  options: string[] | Array<{ option: string } | { Option: string } | Record<string, any>>;
  onOptionClick: (option: string) => void;
}

const ReplyOptionsMessage: React.FC<ReplyOptionsMessageProps> = ({ options, onOptionClick }) => {
  const { language } = useLanguage();
  const { setActiveForm } = useFormContext();
  
  logger.log('🔍 ReplyOptionsMessage received options:', options);
  logger.log('🔍 Options length:', options?.length);
  logger.log('🔍 Options array:', JSON.stringify(options, null, 2));
  
  if (!options || !Array.isArray(options) || options.length === 0) {
    return null;
  }

  // Handle special click for trade-in estimate options
  const handleOptionClick = async (option: string) => {
    logger.log('🔘 BUTTON CLICKED:', option);
    logger.log('🔘 OPTION LENGTH:', option.length);
    logger.log('🔘 OPTION CHAR CODES:', Array.from(option).map(char => char.charCodeAt(0)));
    logger.log('🔘 TRIMMED OPTION:', `"${option.trim()}"`);
    logger.log('🔘 EXACT MATCH CHECK:', option === "Estimé en-ligne en 30sec");
    logger.log('🔘 EXACT MATCH CHECK EN:', option === "Online Estimate in 30sec");
    logger.log('🔘 TRIMMED MATCH CHECK:', option.trim() === "Estimé en-ligne en 30sec");
    logger.log('🔘 INCLUDES CHECK:', option.includes("Estimé en-ligne en 30sec"));
    logger.log('🔘 LOWERCASE INCLUDES:', option.toLowerCase().includes("estimé en-ligne"));
    
    // Check if this is a trade-in estimate option (including specific 30sec variations)
    const isTradeInEstimate = option.trim() === "Online Estimate in 30sec" || 
                              option.trim() === "Estimé en-ligne en 30sec" ||
                              option.toLowerCase().includes("online estimate") || 
                              option.toLowerCase().includes("estimé en-ligne") ||
                              option.toLowerCase().includes("estime en-ligne") || // fallback without accent
                              option.includes("30sec");
    
    logger.log('🎯 IS TRADE ESTIMATE:', isTradeInEstimate);
    
    // Check if this is an in-person estimate option
    const isInPersonEstimate = option.toLowerCase().includes("in-person estimate") || 
                               option.toLowerCase().includes("estimé en personne") ||
                               option.toLowerCase().includes("estime en personne") || // fallback without accent
                               option.toLowerCase().includes("personne");
    
    logger.log('🏢 IS IN-PERSON ESTIMATE:', isInPersonEstimate);
    
    if (isTradeInEstimate) {
      logger.log('✅ HANDLING TRADE ESTIMATE - NOT CALLING onOptionClick');
      // Copy EXACT logic from TradeBanner handleBannerClick
      const isTradeBoostEnabled = DealerManager.isTradeBoostEnabled();
      logger.log('🚀 TradeBoost enabled:', isTradeBoostEnabled);
      
      if (isTradeBoostEnabled) {
        const $ = (window as any).$;
        if ($) {
          try {
            const tradeContainer = $('#TradeInBarPopupContainer');
            if (tradeContainer.length > 0) {
              logger.log('📦 Opening TradeBoost popup');
              // Try different approaches to open the popup
              if (typeof tradeContainer.popupize === 'function') {
                const popupized = tradeContainer.popupize();
                
                // Check if setTrigger method exists
                if (typeof popupized.setTrigger === 'function') {
                  popupized.setTrigger('click').open();
                } else if (typeof popupized.open === 'function') {
                  // Try direct open without setTrigger
                  popupized.open();
                } else {
                  // Fallback: try click trigger directly
                  tradeContainer.trigger('click');
                }
              } else {
                // Fallback: try click trigger directly
                tradeContainer.trigger('click');
              }
            } else {
              throw new Error('TradeInBarPopupContainer not found');
            }
          } catch (error) {
            logger.log('❌ TradeBoost failed, redirecting:', error);
            // Fallback to URL redirect on any error
            window.location.href = language === 'fr' 
              ? '/demandes/vehicule-echange-occasion.html'
              : '/requests/trade-in-used.html';
          }
        } else {
          logger.log('❌ jQuery not available, redirecting');
          // jQuery not available, redirect
          window.location.href = language === 'fr' 
            ? '/demandes/vehicule-echange-occasion.html'
            : '/requests/trade-in-used.html';
        }
      } else {
        logger.log('⏰ Adding delay before redirect');
        // Add a small delay before redirecting to allow any pending operations to complete
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Fallback: Redirect to trade-in page based on language
        const redirectUrl = language === 'fr' 
          ? '/demandes/vehicule-echange-occasion.html'
          : '/requests/trade-in-used.html';
        
        logger.log('🔗 Redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
      }
      
      logger.log('🚫 RETURNING EARLY - NOT CALLING onOptionClick');
      return; // CRITICAL: Don't call onOptionClick
    }
    
    // Handle in-person estimate options
    if (isInPersonEstimate) {
      logger.log('✅ HANDLING IN-PERSON ESTIMATE - LAUNCHING APPOINTMENT FORM');
      setActiveForm('Appointment', { Title: option });
      return; // Don't call external handler
    }
    
    // Default behavior for all other options - call external handler
    // Make sure we don't call onOptionClick for ANY trade estimate options
    logger.log('🔄 CALLING onOptionClick for regular option');
    if (onOptionClick) {
      onOptionClick(option);
    }
  };

  // Convert options to strings if they are objects, filtering out null/undefined values
  const optionStrings = options
    .filter(option => option !== null && option !== undefined) // Filter out null/undefined
    .map(option => {
      logger.log('🔍 Processing option:', option, 'Type:', typeof option);
      if (typeof option === 'string') {
        logger.log('🔍 String option:', `"${option}"`);
        return option;
      } else if (typeof option === 'object' && option) {
        logger.log('🔍 Object option keys:', Object.keys(option));
        // Look for 'Option' key first (capitalized)
        if ('Option' in option && option.Option) {
          logger.log('🔍 Found Option key:', option.Option);
          return option.Option;
        }
        
        // Look for 'option' key (lowercase)
        if ('option' in option && option.option) {
          logger.log('🔍 Found option key:', option.option);
          return option.option;
        }
        
        // Look for numbered option keys (option1, option2, etc.) - case insensitive
        const optionKeys = Object.keys(option).filter(key => 
          key.toLowerCase().startsWith('option')
        );
        if (optionKeys.length > 0) {
          logger.log('🔍 Found numbered option keys:', optionKeys);
          // Sort keys to ensure proper order (option1, option2, option3, etc.)
          const sortedKeys = optionKeys.sort((a, b) => {
            const numA = parseInt(a.toLowerCase().replace('option', '')) || 0;
            const numB = parseInt(b.toLowerCase().replace('option', '')) || 0;
            return numA - numB;
          });
          
          // Return the value of the first option key found
          const value = option[sortedKeys[0]];
          logger.log('🔍 Numbered option value:', value);
          return value;
        }
      }
      const stringOption = String(option);
      logger.log('🔍 Fallback string conversion:', `"${stringOption}"`);
      return stringOption;
    })
    .filter((option, index) => {
      const isValid = option && typeof option === 'string' && option.trim().length > 0;
      logger.log(`🔍 Filter check [${index}]:`, `"${option}"`, 'Valid:', isValid);
      return isValid;
    }); // Enhanced filter

  logger.log('🔍 Final optionStrings:', optionStrings);

  // If no valid options remain after filtering, don't render anything
  if (optionStrings.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 space-y-2">
      {optionStrings.map((option, index) => (
        <Button
          key={index}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            logger.log('🚨 RAW BUTTON CLICK DETECTED:', option);
            handleOptionClick(option);
          }}
          className="w-full max-w-full h-auto whitespace-normal break-words bg-black hover:bg-gray-800 text-white transition-all duration-300 hover:scale-105 hover:shadow-lg text-sm py-2"
        >
          {option}
        </Button>
      ))}
    </div>
  );
};

export default ReplyOptionsMessage;
